import React from 'react'

import Reflux from 'reflux-react-16'
import moment from 'moment'
import _ from 'underscore'

import ShiftStore from '../../stores/ShiftStore.js'
import ShiftActions from '../../actions/ShiftActions.js'
import UserStore from '../../stores/UserStore.js'
import CustomerStore from 'src/stores/CustomerStore.js'
import OrderStore from 'src/stores/OrderStore.js'

import EditShiftModal from '../../components/shifts/EditShiftModal.js'
import {Button, Colors, Icon, IconButton, P, Panel, Popup, Row, Tab, Tabs, ToolTip} from '../../components/UI/index.js'
import {formSpacing, Select} from '../../components/UI/forms/index.js'
import Table from '../../components/UI/table/index.js'
import SearchBar from '../../components/SearchBar/index.js'
import csvExport from '../../utils/csvExport.js'
import filter from '../../utils/filter.js'
import {getPriceTableName} from '../../utils/priceTables.js'
import {emit} from 'src/socketio/socketEmit.js'
import ViewShiftModal from '@components/shifts/ViewShiftModal.js'
import time from 'server/utils/time.js'
import Languages from '@utils/Languages.js'
import NewShift from '@components/shifts/NewShift.js'
import {formatDuration} from '@utils/utils.js'
import t from 'src/helpers/Languages.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import createMemo from '../../utils/createMemo.js'

export default class Shifts extends Reflux.Component {
    constructor(props) {
        super(props)

        this.columnsMemo = createMemo()

        this.stores = [ShiftStore, UserStore, CustomerStore, OrderStore]
    }

    componentDidMount() {
        const {reseller} = this.props
        let {shiftId = ''} = this.props.match.params
        document.title = `Diensten • ${reseller.settings.accountName || reseller.name || ''}`

        if (this.props.location?.state?.shiftId) {
            this.onClickView(this.props.location?.state?.shiftId)
        }

        if (shiftId) {
            this.onClickView(shiftId)
        }

        // The 'too late' icons of running shifts depend on the current time: rebuild the columns every minute
        this.minuteInterval = setInterval(() => this.setState({minuteTick: Date.now()}), 60 * 1000)
    }

    componentWillUnmount() {
        super.componentWillUnmount()
        clearInterval(this.minuteInterval)
    }

    componentDidUpdate() {
        if (this.state.shiftLimitWarning) {
            this.setState({shiftLimitWarning: false})
            this.alertPopup.openAlert('Waarschuwing', 'Je huidige datumrange bevat meer dan 35 000 diensten, hierdoor worden niet alle diensten weergegeven. Verklein de datumrange om dit te voorkomen. ')
        }
    }

    // The same columns until a value they read changes: new columns re-render every row of the table
    columns() {
        return this.columnsMemo([this.props.reseller, this.state.customers, this.state.usersObject, this.state.minuteTick], () => this.buildColumns())
    }

    buildColumns() {
        const {reseller} = this.props

        return [
            ...branchesAvailable(reseller) ?
                [{id: 'branchName', title: t('branch'), visible: false, key: 'branchName', width: 180, text: (shift) => {
                    const branch = reseller.settings.branches?.find((branch) => branch.id === shift?.branchId)
                    return branch?.name || ''
                }}] :
                [],
            ...(reseller.settings.shifts?.shiftbase?.enabled ?
                [{
                    id: 'shiftbaseStatus', title: 'Shiftbase status', visible: true, sort: (shift) => {
                        if (shift.meta?.syncedWithShiftbase) {
                            return 1
                        } else if (shift.meta?.shiftbaseError) {
                            return 2
                        } else {
                            return 3
                        }
                    },
                    render: (shift) => {
                        if (shift.meta?.syncedWithShiftbase) {
                            return (
                                <Icon size='xs' icon='mdi mdi-circle' style={{marginRight: 6, color: Colors.successBright}} />
                            )
                        } else if (shift.meta?.shiftbaseError) {
                            return (
                                <ToolTip placement='right' text={shift.meta.shiftbaseError}>
                                    <Icon size='xs' icon='mdi mdi-circle' style={{marginRight: 6, color: Colors.errorBright}} />
                                </ToolTip>
                            )
                        } else {
                            return (
                                <Icon size='xs' icon='mdi mdi-circle' style={{marginRight: 6, color: Colors.grey30}} />
                            )
                        }
                    }
                }] :
                []),
            {id: 'date', title: Languages.date['NL'], visible: false, key: 'date', width: 120, text: (shift) => {
                return moment(shift.date).format('DD-MM-YYYY')
            }, sort: (shift) => shift.date},
            {id: 'name', title: Languages.name['NL'], visible: true, flex: 1, key: 'name'},
            {id: 'customerId', title: Languages.customer['NL'], visible: true, key: 'customerId', width: 300, text: (shift) => {
                const customer = this.state.customers[shift.customerId]
                return customer?.name || ''
            }},
            {id: 'messengerId', title: Languages.messenger['NL'], visible: true, key: 'messengerId', width: 300, text: (shift) => {
                const messenger = this.state.usersObject[shift.messengerId]
                return messenger?.name || ''
            }},
            {id: 'plannedStartTime', title: Languages.startTime['NL'], visible: true, width: 150, key: 'plannedStartTime'},
            {id: 'startTime', title: Languages.startTimeExecuted['NL'], visible: true, width: 150, key: 'startTime', render: (shift) => {
                const startTime = shift.startTime || time.timestamp()

                const tooLate = shift.plannedStartTime && moment(startTime).isAfter(moment(`${shift.date} ${shift.plannedStartTime}`))

                return (
                    <Row style={{alignItems: 'center'}}>
                        {tooLate && <Icon icon='mdi mdi-alert' style={{marginRight: 6, color: Colors.errorBright}} />}
                        <P>{shift.startTime ? moment(shift.startTime).format('HH:mm') : ''}</P>
                    </Row>
                )
            }},
            {id: 'plannedEndTime', title: Languages.endTime['NL'], visible: true, width: 150, key: 'plannedEndTime'},
            {id: 'endTime', title: Languages.endTimeExecuted['NL'], visible: true, width: 150, key: 'endTime', render: (shift) => {
                const endTime = shift.endTime || time.timestamp()

                const tooLate = shift.plannedEndTime && moment(endTime).isAfter(moment(`${shift.date} ${shift.plannedEndTime}`))

                return (
                    <Row style={{alignItems: 'center'}}>
                        {tooLate && <Icon icon='mdi mdi-alert' style={{marginRight: 6, color: Colors.errorBright}} />}
                        <P>{shift.endTime ? moment(shift.endTime).format('HH:mm') : ''}</P>
                    </Row>
                )
            }},
            {id: 'workedTime', title: Languages.workedTime['NL'], visible: false, width: 150, key: 'workedTime', text: (shift) => {
                return shift.endTime ? formatDuration(shift.workedTime) : ''
            }},
            {id: 'pausedTime', title: Languages.pausedTime['NL'], visible: false, width: 150, key: 'pausedTime', text: (shift) => {
                return shift.endTime ? formatDuration(shift.pausedTime || 0) : ''
            }
            },
            {id: 'price', title: Languages.price['NL'], visible: false, key: 'price', text: (shift) => {
                return `€ ${shift.price}`
            }},
            {id: 'priceTableId', title: Languages.priceTable['NL'], visible: false, key: 'priceTableId', text: (shift) => {
                return getPriceTableName(reseller.settings.shifts.prices, shift.priceTableId)
            }},
            {id: 'startPoint', title: Languages.startLocation['NL'], visible: false, key: 'startPoint', width: 150, text: (shift) => {
                return shift.startPoint ? `${shift.startPoint?.street || ''} ${shift.startPoint?.nr || ''}${shift.startPoint?.addtion || ''}, ${shift.startPoint?.city || ''}` : ''
            }},
            {id: 'endPoint', title: Languages.endLocation['NL'], visible: false, key: 'endPoint', width: 150, text: (shift) => {
                return shift.endPoint ? `${shift.endPoint?.street || ''} ${shift.endPoint?.nr || ''}${shift.endPoint?.addtion || ''}, ${shift.endPoint?.city || ''}` : ''
            }},
            {id: 'status', title: Languages.status['NL'], visible: true, flex: 2, key: 'status', render: (shift) => {
                const {status, color} = Colors.shiftStatus(reseller, shift)

                return (
                    <P ellipsis style={{width: '100%', padding: 5, textAlign: 'center', background: color, borderRadius: 4}}>{status}</P>
                )
            }, sort: (shift) => Colors.shiftStatus(reseller, shift)?.status},
            {id: 'view', visible: true, showOnHover: true, width: 50, render: (order) => {
                return (
                    <IconButton onClick={this.onClickView.bind(this, order._id)}>
                        <i className='mdi mdi-eye' />
                    </IconButton>
                )
            }}
        ]
    }

    onChangeDates(startDate, endDate) {
        ShiftActions.setDates(startDate, endDate)
    }

    onChangeQueries(shiftQueries) {
        this.table.selectPage(1)
        ShiftActions.setQueries(shiftQueries)
    }

    onChangeTable(selectedShifts) {
        ShiftActions.setSelected(selectedShifts)
    }

    onClickNew() {
        this.modal.open()
        ShiftActions.setSelected([])
    }

    onClickCopy(id) {
        const {shifts} = this.state
        const {reseller} = this.props

        let originalShift = _.findWhere(shifts, {_id: id})

        const shift = NewShift(reseller)

        shift.date = originalShift.date
        shift.name = originalShift.name
        shift.customerId = originalShift.customerId
        shift.messengerId = originalShift.messengerId
        shift.plannedStartTime = originalShift.plannedStartTime
        shift.plannedEndTime = originalShift.plannedEndTime
        shift.startLocation = originalShift.startLocation
        shift.endLocation = originalShift.endLocation
        shift.instructions = originalShift.instructions
        shift.targets = originalShift.targets

        this.modal.open(shift)
        ShiftActions.setSelected([])
    }

    onClickView(id) {
        this.viewShiftModal.open(id)
        ShiftActions.setSelected([])
    }

    async onClickEdit(id) {
        let {data: shift} = await emit('shifts.findById', id)

        if (!shift) {
            const {shifts} = this.state
            shift = _.findWhere(shifts, {_id: id})
        }

        this.modal.open(shift)
        ShiftActions.setSelected([])
    }

    async onClickRemove(id) {
        const {selectedShifts} = this.state

        if (id) {
            if (!await this.popup.open('Dienst verwijderen', 'Dienst wordt definitief verwijderd.')) return
        } else {
            if (!await this.popup.open('Diensten verwijderen', 'Diensten worden definitief verwijderd.')) return
        }

        try {
            await emit('shifts.remove', id ? [id] : selectedShifts)
            ShiftActions.setSelected([])
            this.popup.close()
        } catch (err) {
            this.popup.setError(err.message)
        }
    }

    async onClickExport() {
        const {customers, usersObject, shifts, selectedShifts, startDate, endDate} = this.state
        const {reseller} = this.props
        const exportShifts = []

        selectedShifts.map((id) => {
            const shift = _.findWhere(shifts, {_id: id})
            if (shift) {
                exportShifts.push(shift)
            }
        })

        await this.popup.openProgress(t('export'), '', (onProgress) => csvExport.shifts(exportShifts, startDate, endDate, reseller, customers, usersObject, onProgress))

        ShiftActions.setSelected([])
    }

    async onChangeMessenger(event, id) {
        const {selectedShifts} = this.state

        let ids = id ? [id] : selectedShifts

        try {
            await emit('shifts.setMessenger', {ids, messengerId: event.target.value})
        } catch (err) {
            this.popup.setError(err.message)
            this.popup.open()
        }

        ShiftActions.setSelected([])
    }

    async onClickCalculatePrice() {
        const {selectedShifts} = this.state
        const {reseller} = this.props

        try {
            this.popup.setInputs([
                {
                    type: 'select',
                    name: 'priceTableId',
                    label: 'Tarieftabel',
                    options: [
                        {value: '', title: 'Behoud huidige tarieftabel'},
                        {value: 'remove', title: 'Verwijder tarieftabel'},
                        ...(reseller.settings.shifts.prices || []).map((priceTable) => ({value: priceTable.id, title: priceTable.name}))
                    ]
                }
            ])
            const values = await this.popup.open('Dienst tarieven berekenen', '', undefined, undefined, true)
            if (!values) return
            const {errors} = await emit('shifts.calculatePrice', {ids: selectedShifts, priceTableId: values.priceTableId})

            if (errors?.length) {
                this.popup.setErrors(errors)
            } else {
                this.popup.close()
            }
        } catch (err) {
            this.popup.setError(err.message)
        }

        ShiftActions.setSelected([])
    }

    render() {
        const {selectedShifts, shiftsLoading, shiftQueries, users, usersObject, customers, startDate, endDate, routeTemplates} = this.state
        const {reseller, history} = this.props
        const shifts = filter.shifts(this.state.shifts, shiftQueries, reseller, customers, usersObject)

        return (
            <div style={{display: 'flex', flexDirection: 'column', height: '100%'}}>
                <Panel style={{height: 40, borderTop: 'none', borderLeft: 'none', borderRight: 'none'}}>
                    <Tabs
                        style={{width: 400, marginBottom: 0}}
                        value={history.location.pathname}
                        onChange={(event) => {
                            history.push(event.target.value)
                        }}
                    >
                        <Tab value='/diensten'>Diensten</Tab>
                        <Tab value='/diensttemplates'>Dienst templates</Tab>
                    </Tabs>
                </Panel>

                <Panel style={{display: 'flex', columnGap: formSpacing.columnGap, borderTop: 'none', borderLeft: 'none', borderRight: 'none', background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>

                    <SearchBar
                        columns={this.columns()}
                        onQueryChange={this.onChangeQueries.bind(this)}
                        onDateChange={this.onChangeDates.bind(this)}
                        startDate={startDate}
                        endDate={endDate}
                        queries={shiftQueries}
                    />

                    <Button
                        variant='outline-white'
                        onClick={this.onClickNew.bind(this)}
                    >
                        Nieuwe Dienst
                    </Button>

                    {reseller.settings.shifts?.shiftbase?.enabled &&
                        <>
                            <Button
                                variant='outline-white'
                                label='Verstuur naar Shiftbase'
                                onClick={async () => {
                                    this.popup.setInputs([
                                        {
                                            type: 'date',
                                            label: 'Datum',
                                            name: 'date',
                                            value: this.state.startDate
                                        },
                                        {
                                            type: 'select',
                                            label: 'Koerier',
                                            name: 'messengerId',
                                            options: [
                                                {value: '', title: 'Alle koeriers'},
                                                ...users.map((user) => {
                                                    return {value: user._id, title: user.name}
                                                })
                                            ]
                                        }
                                    ])

                                    const values = await this.popup.open('Diensten versturen', 'Selecteer de koerier wiens diensten je wilt versturen naar Shiftbase. Als je geen koerier selecteert, worden alle diensten verstuurd.', undefined, undefined, true)
                                    if (!values) return

                                    try {
                                        const {errors} = await emit('shifts.sendToShiftbase', {date: values.date, messengerId: values.messengerId})

                                        if (errors?.length) {
                                            this.popup.setErrors(errors)
                                        } else {
                                            this.popup.close()
                                        }
                                    } catch (err) {
                                        this.popup.setError(err.message)
                                    }
                                }}
                            />

                            <Button
                                variant='outline-white'
                                label='Synchroniseer met Shiftbase'
                                onClick={async () => {
                                    this.popup.setInputs([
                                        {
                                            type: 'date',
                                            label: Languages.startDate['NL'],
                                            name: 'startDate',
                                            value: this.state.startDate
                                        },
                                        {
                                            type: 'date',
                                            label: Languages.endDate['NL'],
                                            name: 'endDate',
                                            value: this.state.endDate
                                        }
                                    ])

                                    const values = await this.popup.open('Diensten Synchroniseren', '', undefined, undefined, true)
                                    if (!values) return

                                    try {
                                        const {errors} = await emit('shifts.syncFromShiftbase', {startDate: values.startDate, endDate: values.endDate})

                                        if (errors?.length) {
                                            this.popup.setErrors(errors)
                                        } else {
                                            this.popup.close()
                                        }
                                    } catch (err) {
                                        this.popup.setError(err.message)
                                    }
                                }}

                            />
                        </>
                    }

                    {selectedShifts.length === 1 &&
                        <Button
                            variant='outline-white'
                            onClick={() => this.onClickCopy(selectedShifts[0])}
                        >
                            <i className='mdi mdi-content-copy' />
                        </Button>
                    }

                    {selectedShifts.length > 0 &&
                        <>
                            <Button
                                variant='outline-white'
                                onClick={this.onClickExport.bind(this)}
                            >
                                <i className='mdi mdi-file-export' />
                            </Button>

                            <Button
                                variant='outline-white'
                                onClick={() => this.onClickCalculatePrice()}
                            >
                                <i className='mdi mdi-currency-eur' />
                            </Button>

                            <Button
                                variant='outline-white'
                                onClick={() => this.onClickRemove()}
                            >
                                <i className='mdi mdi-delete' />
                            </Button>

                            <Select
                                style={{width: 160, marginLeft: 6}}
                                label='Wijzig koerier'
                                allowEmpty
                                options={users.map((user) => {
                                    return {value: user._id, title: user.name}
                                })}
                                onChange={(event) => this.onChangeMessenger(event)}
                            />
                        </>
                    }
                </Panel>

                <div style={{flex: 1, marginTop: 24, marginRight: 24, marginLeft: 24}}>
                    <Table
                        tableName='shifts'
                        columns={this.columns()}
                        rows={shifts}
                        selectedRows={selectedShifts}
                        loading={shiftsLoading}
                        onChange={this.onChangeTable.bind(this)}
                        ref={(ref) => this.table = ref}
                    />
                </div>

                <EditShiftModal
                    reseller={reseller}
                    customers={customers}
                    users={usersObject}
                    routeTemplates={routeTemplates}
                    onClickView={this.onClickView.bind(this)}
                    ref={(ref) => this.modal = ref}
                />

                <ViewShiftModal
                    reseller={reseller}
                    customers={customers}
                    users={usersObject}
                    onClickCopy={this.onClickCopy.bind(this)}
                    onClickEdit={this.onClickEdit.bind(this)}
                    onClickRemove={this.onClickRemove.bind(this)}
                    ref={(ref) => this.viewShiftModal = ref}
                    history={this.props.history}
                    match={this.props.match}
                />

                <Popup ref={(ref) => this.popup = ref} />
                <Popup ref={(ref) => this.alertPopup = ref} />
            </div>

        )
    }
}
